import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { Download, ExternalLink } from 'lucide-react'
import { toast } from 'sonner'
import { type CompetitorListKind, useManufacturerCompetitorPrices } from '@/api/hooks'
import { API_BASE_URL } from '@/api/client'
import type { components } from '@/api/schema'
import { LoadingButton } from '@/components/loading-button'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useAuth } from '@/hooks/use-auth'
import { sellerName } from '@/lib/retailers'
import { formatMoney, parseUTCDate } from '@/lib/utils'
import { getApiErrorMessage } from '@/lib/api-error'

type Row = components['schemas']['CompetitorListRowRead']

const LISTS: Record<CompetitorListKind, { title: string; empty: string; noun: string }> = {
  'below-floor': {
    title: 'Below our floor',
    empty: 'No retailer sells these products below our floor.',
    noun: 'product',
  },
  'below-map': {
    title: 'Sellers below MAP',
    empty: 'No retailer sells these products below MAP.',
    noun: 'seller listing',
  },
}

function seenDate(value: string | null | undefined): string {
  if (!value) return '-'
  return parseUTCDate(value).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  })
}

/** Products a retailer sells below our floor and sellers below MAP, with Excel exports. */
export function ManufacturerCompetitorPrices({ manufacturerId }: { manufacturerId: string }) {
  return (
    <div className="space-y-4">
      <CompetitorList manufacturerId={manufacturerId} kind="below-floor" />
      <CompetitorList manufacturerId={manufacturerId} kind="below-map" />
    </div>
  )
}

function CompetitorList({
  manufacturerId,
  kind,
}: {
  manufacturerId: string
  kind: CompetitorListKind
}) {
  const [page, setPage] = useState(1)
  const { data, isLoading, isError } = useManufacturerCompetitorPrices(manufacturerId, kind, page)
  const list = LISTS[kind]
  const total = data?.total ?? 0
  const headingId = `competitor-${kind}`

  return (
    <section aria-labelledby={headingId} className="overflow-hidden rounded-xl border bg-card">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b px-4 py-3">
        <h2 id={headingId} className="text-sm font-semibold">
          {list.title}
          {total > 0 && (
            <span className="ml-2 font-normal tabular-nums text-muted-foreground">
              {total.toLocaleString()} {list.noun}
              {total === 1 ? '' : 's'}
            </span>
          )}
        </h2>
        {total > 0 && <ExportButton manufacturerId={manufacturerId} kind={kind} />}
      </div>
      {isLoading ? (
        <div className="space-y-2 p-4">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
        </div>
      ) : isError ? (
        <p className="px-4 py-6 text-sm text-destructive">
          This list didn't load. Refresh the page to try again.
        </p>
      ) : total === 0 ? (
        <p className="px-4 py-6 text-sm text-muted-foreground">{list.empty}</p>
      ) : (
        <>
          {kind === 'below-floor' ? (
            <BelowFloorTable rows={data?.items ?? []} />
          ) : (
            <BelowMapTable rows={data?.items ?? []} />
          )}
          {(data?.pages ?? 1) > 1 && (
            <div className="flex items-center justify-between border-t px-4 py-2">
              <p className="text-xs text-muted-foreground tabular-nums">
                Page {page} of {data?.pages}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={page >= (data?.pages ?? 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </section>
  )
}

function ExportButton({
  manufacturerId,
  kind,
}: {
  manufacturerId: string
  kind: CompetitorListKind
}) {
  const { getAccessToken } = useAuth()
  const [exporting, setExporting] = useState(false)

  const exportList = async () => {
    setExporting(true)
    try {
      const token = await getAccessToken()
      if (!token) throw new Error('Sign in again to export.')
      const response = await fetch(
        `${API_BASE_URL}/dashboard/manufacturers/${manufacturerId}/competitor-prices/${kind}/export`,
        { headers: { Authorization: `Bearer ${token}` } },
      )
      if (!response.ok) throw new Error(`Export failed (${response.status}). Try again.`)
      const disposition = response.headers.get('Content-Disposition') ?? ''
      const filename = /filename="([^"]+)"/.exec(disposition)?.[1] ?? `${kind}.xlsx`
      const href = URL.createObjectURL(await response.blob())
      const link = document.createElement('a')
      link.href = href
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(href)
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Export failed. Try again.'))
    } finally {
      setExporting(false)
    }
  }

  return (
    <LoadingButton
      variant="outline"
      size="sm"
      loading={exporting}
      loadingText="Exporting..."
      onClick={exportList}
    >
      <Download className="h-4 w-4" /> Export to Excel
    </LoadingButton>
  )
}

function ProductCell({ row }: { row: Row }) {
  return (
    <TableCell className="max-w-[11rem] pl-4 sm:max-w-[22rem]">
      <Link
        to="/products/$productId"
        params={{ productId: row.product_uuid }}
        className="font-medium hover:underline"
      >
        {row.model}
      </Link>
      {row.store_title && (
        <p className="truncate text-xs text-muted-foreground" title={row.store_title}>
          {row.store_title}
        </p>
      )}
      <p className="truncate text-xs text-muted-foreground sm:hidden">
        {sellerName(row.seller)} {formatMoney(row.their_price)} · {seenDate(row.observed_at)}
      </p>
    </TableCell>
  )
}

function SellerCell({ row }: { row: Row }) {
  return (
    <TableCell className="hidden sm:table-cell">
      {row.url ? (
        // eslint-disable-next-line no-restricted-syntax -- external retailer listing
        <a
          href={row.url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 hover:underline"
        >
          {sellerName(row.seller)}
          <ExternalLink className="h-3 w-3 text-muted-foreground" aria-hidden />
        </a>
      ) : (
        sellerName(row.seller)
      )}
      <p className="text-xs text-muted-foreground">Seen {seenDate(row.observed_at)}</p>
    </TableCell>
  )
}

function BelowFloorTable({ rows }: { rows: Row[] }) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="pl-4">Product</TableHead>
          <TableHead className="hidden text-right md:table-cell">NET</TableHead>
          <TableHead className="hidden text-right md:table-cell">Floor</TableHead>
          <TableHead className="hidden sm:table-cell">Retailer</TableHead>
          <TableHead className="hidden text-right sm:table-cell">Retailer price</TableHead>
          <TableHead className="pr-4 text-right">NET to match</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={`${row.product_uuid}-${row.seller}`}>
            <ProductCell row={row} />
            <TableCell className="hidden text-right tabular-nums md:table-cell">
              {formatMoney(row.net)}
            </TableCell>
            <TableCell className="hidden text-right tabular-nums md:table-cell">
              {formatMoney(row.floor_price)}
            </TableCell>
            <SellerCell row={row} />
            <TableCell className="hidden text-right tabular-nums sm:table-cell">
              {formatMoney(row.their_price)}
            </TableCell>
            <TableCell className="pr-4 text-right font-medium tabular-nums">
              {formatMoney(row.net_to_match)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

function BelowMapTable({ rows }: { rows: Row[] }) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="pl-4">Product</TableHead>
          <TableHead className="hidden sm:table-cell">Seller</TableHead>
          <TableHead className="hidden text-right sm:table-cell">Seller price</TableHead>
          <TableHead className="hidden text-right md:table-cell">MAP</TableHead>
          <TableHead className="pr-4 text-right">Below MAP by</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={`${row.product_uuid}-${row.seller}`}>
            <ProductCell row={row} />
            <SellerCell row={row} />
            <TableCell className="hidden text-right tabular-nums sm:table-cell">
              {formatMoney(row.their_price)}
            </TableCell>
            <TableCell className="hidden text-right tabular-nums md:table-cell">
              {formatMoney(row.map_price)}
            </TableCell>
            <TableCell className="pr-4 text-right font-medium tabular-nums text-destructive">
              {formatMoney(row.below_by)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}
